import { isTechnicalInstruction } from "utils/contentful-types";
import { notUndefined } from "utils";
import { stripMarkdown } from "utils/markdown";

type ContentfulQuestion = GatsbyTypes.Maybe<
  Pick<GatsbyTypes.contentfulFaqItemQuestionTextNode, "question">
>;

type ContentfulRelatedContent = GatsbyTypes.Maybe<
  ReadonlyArray<
    GatsbyTypes.Maybe<Pick<GatsbyTypes.ContentfulLink, "url" | "text" | "id">>
  >
>;

type ContentfulAnswer = GatsbyTypes.Maybe<
  | GatsbyTypes.BuildStepContentNode_ContentfulCodeExample_Fragment
  | GatsbyTypes.BuildStepContentNode_ContentfulCodeExamples_Fragment
  | GatsbyTypes.BuildStepContentNode_ContentfulTechnicalDiagram_Fragment
  | GatsbyTypes.BuildStepContentNode_ContentfulTechnicalInstruction_Fragment
>;

export type ContentfulFaqItem = Pick<
  GatsbyTypes.ContentfulFaqItem,
  "id" | "topics" | "slug" | "order"
> & {
  readonly question: ContentfulQuestion;
  readonly answer: GatsbyTypes.Maybe<ReadonlyArray<ContentfulAnswer>>;
  readonly relatedContent: ContentfulRelatedContent;
};

interface RelatedContent {
  id: string;
  url: string;
  text: string;
}

export interface FaqItem {
  id: string;
  slug: string;
  question: string;
  topics: string[];
  relatedContent: RelatedContent[];
  summary?: string;
  answer: ContentfulAnswer[];
  order?: number;
  __typename?: string;
}

const removeEmptyItemsAndStringifyRemaining = (
  items: readonly (string | undefined | null)[] | undefined
): string[] =>
  items
    ? items.reduce(
        (list: string[], item) =>
          typeof item === "undefined" ? list : [...list, String(item)],
        []
      )
    : [];

const removeEmptyRelatedContentAndRemainingAsRelatedContent = (
  relatedContentItems: ContentfulRelatedContent
): RelatedContent[] =>
  relatedContentItems
    ? relatedContentItems.reduce(
        (list: RelatedContent[], relatedContent) =>
          typeof relatedContent !== "undefined" &&
          relatedContent?.text &&
          relatedContent.url
            ? [
                ...list,
                {
                  text: relatedContent.text,
                  url: relatedContent.url,
                  id: relatedContent.id
                }
              ]
            : list,
        []
      )
    : [];

//
const getFirstTechnicalInstructionBody = (
  answerList: ReadonlyArray<ContentfulAnswer | undefined> | undefined
): string | undefined => {
  if (!answerList) return undefined;

  const technicalInstruction = answerList?.find(
    (content) =>
      content && content.__typename === "ContentfulTechnicalInstruction"
  );

  if (!isTechnicalInstruction(technicalInstruction)) return undefined;

  return (
    technicalInstruction.childContentfulTechnicalInstructionBodyTextNode
      ?.body ?? undefined
  );
};

export type LocalContentfulFaqItem = Pick<
  ContentfulFaqItem,
  "id" | "slug" | "topics" | "order"
> & {
  readonly question: GatsbyTypes.Maybe<
    Pick<GatsbyTypes.contentfulFaqItemQuestionTextNode, "question">
  >;
  readonly answer: GatsbyTypes.Maybe<
    ReadonlyArray<
      GatsbyTypes.Maybe<
        | GatsbyTypes.BuildStepContentNode_ContentfulCodeExample_Fragment
        | GatsbyTypes.BuildStepContentNode_ContentfulCodeExamples_Fragment
        | GatsbyTypes.BuildStepContentNode_ContentfulTechnicalDiagram_Fragment
        | GatsbyTypes.BuildStepContentNode_ContentfulTechnicalInstruction_Fragment
        | GatsbyTypes.BuildStepContentNode_ContentfulTable_Fragment
        | GatsbyTypes.BuildStepContentNode_ContentfulPaymentLogo_Fragment
        | GatsbyTypes.BuildStepContentNode_ContentfulPaymentMethodLogo_Fragment
        | GatsbyTypes.BuildStepContentNode_ContentfulTrait_Fragment
      >
    >
  >;
  readonly relatedContent: GatsbyTypes.Maybe<
    ReadonlyArray<GatsbyTypes.Maybe<GatsbyTypes.LinkNodeFragment>>
  >;
  readonly __typename?: string | undefined;
};
// build a faq item that has been cleaned up or return undefined if were missing
// a question or answer or slug
export const buildFaqItem = (
  item: LocalContentfulFaqItem
): FaqItem | undefined => {
  const { id, slug, __typename } = item;
  const question = item?.question?.question;
  const answer = Array.isArray(item.answer) ? [...item.answer] : undefined;
  const firstTechnicalInstruction = getFirstTechnicalInstructionBody(answer);
  const strippedFirstTechnicalInstruction = stripMarkdown(
    firstTechnicalInstruction || ""
  );
  const firstSentance = strippedFirstTechnicalInstruction?.split("\n")[0];
  const summary =
    firstSentance && firstSentance.length > 200
      ? `${firstSentance?.slice(0, 200)}…`
      : firstSentance;
  const topics = removeEmptyItemsAndStringifyRemaining(
    item.topics ?? undefined
  );
  const relatedContent = removeEmptyRelatedContentAndRemainingAsRelatedContent(
    item.relatedContent
  );
  const order = item?.order;

  return notUndefined(question) &&
    question &&
    notUndefined(answer) &&
    answer &&
    notUndefined(slug) &&
    slug
    ? {
        id,
        question,
        answer,
        summary,
        slug,
        topics,
        relatedContent,
        order: order ?? -1,
        __typename
      }
    : undefined;
};

export const getAvailableTopics = (items: FaqItem[]): string[] =>
  items
    ? items.reduce((list: string[], item) => {
        const uniqueTopics = item.topics.filter(
          (topic) => !list.includes(topic)
        );
        return [...list, ...uniqueTopics];
      }, [])
    : [];

export const sortByOrder = (
  a: { order?: number },
  b: { order?: number }
): number => {
  return (a.order ?? 0) - (b.order ?? 0);
};
